import { memo, useEffect } from "react";
import { Table, TableProps, Image, Button, Space } from "antd";
import { useTranslation } from "react-i18next";
import { mdlOffer } from "../../models/domain/Offer";
import { faBan, faCalendar, faCheck, faClock, faEye, faHome, faHotel, faHourglass, faKey, faPaperPlane, faThumbsDown, faTimes, faTrophy, faUser, faUsers, faWarning } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { enmOfferAnswerType } from "../../models/enums/OfferAnswerType";
import dayjs from "dayjs";

export type OfferTableProps = {
    onTableItemSelect?: any;
    tableItems?: mdlOffer[];
    selectedOfferList?: string[];
    selectedOffer?: mdlOffer;
}

export const OfferTable = memo(function OfferTable(props: OfferTableProps) {
    const { t } = useTranslation();

    function showDetail(record: mdlOffer) {
        props.onTableItemSelect && props.onTableItemSelect(record);
    }

    const setRemainingTimes = (dateNow:Date, dateOfferEnd:Date) => {
        var totMinute = (dateOfferEnd.getTime() - dateNow.getTime()) / 1000 / 60;
        var day = (totMinute / (60 * 24)).toString();
        var _day = totMinute % (60 * 24);
        var hour = (_day / 60).toString();
        var _hour = (_day % 60).toString();
        return [parseInt(day, 10), parseInt(hour, 10), parseInt(_hour, 10)];
    }
    
    const checkTOut = (offer:mdlOffer | undefined) => {
        if(offer == undefined || offer.dayHourMin == undefined || offer.dayHourMin == null) {
            return false;
        }
        var dateNow = new Date(new Date().getTime());
        var dateOfferEnd = new Date(dateNow.setDate(dateNow.getDate() + parseInt(offer.dayHourMin[0], 10)));
        dateOfferEnd = new Date(dateOfferEnd.setHours(dateNow.getHours() + parseInt(offer.dayHourMin[1], 10)));
        dateOfferEnd = new Date(dateOfferEnd.setMinutes(dateNow.getMinutes() + parseInt(offer.dayHourMin[2], 10)));
        var t = setRemainingTimes(new Date(), dateOfferEnd);
        if (t[0] < 0 || t[1] < 0 || t[2] < 0) {
            return true;
        }
        return false;
    }
    
    const getRemainingTimes = (offer:mdlOffer | undefined) => {
        if(offer == undefined || offer.dayHourMin == undefined || offer.dayHourMin == null) {
            return null;
        }
        var dateNow = new Date(new Date().getTime());
        var dateOfferEnd = new Date(dateNow.setDate(dateNow.getDate() + parseInt(offer.dayHourMin[0], 10)));
        dateOfferEnd = new Date(dateOfferEnd.setHours(dateNow.getHours() + parseInt(offer.dayHourMin[1], 10)));
        dateOfferEnd = new Date(dateOfferEnd.setMinutes(dateNow.getMinutes() + parseInt(offer.dayHourMin[2], 10)));
        return setRemainingTimes(new Date(), dateOfferEnd);
    }

    const columns: TableProps<mdlOffer>['columns'] = [
        {
            title: t('common.image'),
            width: 150,
            key: 'image',
            render: (_, record) => (
                <>
                    <img src={record.agencyLogoUrl} alt={record.agencyName} title={record.agencyName} />
                </>
            ),
        },
        {
            title: t('common.detail'),
            key: 'detail',
            render: (_, record) => (
                <>
                    <div className="text-start">
                        <div>
                            <span>
                                <FontAwesomeIcon icon={faHome}></FontAwesomeIcon>
                            </span>
                            <label className="ps-2">
                                {record.hotelName} - {t("offer.offer")} : {record.offerNo}
                            </label>
                        </div>
                        <div>
                            <span>
                                <FontAwesomeIcon icon={faCalendar}></FontAwesomeIcon>
                            </span>
                            <label className="ps-2">
                                {dayjs(record.resStartDate).format("DD.MM.YYYY")}
                                <span> / </span>
                                {dayjs(record.resEndDate).format("DD.MM.YYYY")}
                                <span> / </span>
                                {record.night}
                            </label>
                            <label className="ps-1">
                                {t("offer.night")}
                            </label>
                        </div>
                        <div>
                            <span>
                                <FontAwesomeIcon icon={record.isGroup ? faUsers : faUser} />
                            </span>
                            {record.isGroup ?
                                <label className="ps-2">
                                    {t("offer.groupReservation")}
                                </label>
                                :
                                <label className="ps-2">
                                    {record.adult} {t("offer.adult")} {record.child && record.child > 0 ? record.child + ' ' + t("offer.child") : ""}
                                </label>
                            }
                        </div>
                        <div>
                            {record.isGroup ? <></> : <>
                                <span>
                                    <FontAwesomeIcon icon={faKey}></FontAwesomeIcon>
                                </span>
                                <label className="ps-2">
                                    {record.roomTypeName}
                                </label>
                            </>
                            }
                        </div>
                        {
                            record.dayHourMin?.length === 3 &&
                            <div className="text-danger fw-bold">
                                <span>
                                    <FontAwesomeIcon icon={faHourglass}></FontAwesomeIcon>
                                </span>
                                <label className="ps-2">
                                    {record.dayHourMin[0]} {t("offer.days")} {record.dayHourMin[1]} {t("offer.hours")} {record.dayHourMin[2]} {t("offer.minutes")}
                                </label>
                            </div>
                        }
                    </div>
                    <div className="mt-2">
                        <div className="d-inline-block me-2 mb-2">
                            <label className="d-block bg-outline-secondary br-0">
                                <span className="badge bg-secondary w-100 d-block br-0">
                                    {
                                        record.priceType == 0
                                            ? <span className="price">{t("offer.contract")}</span>
                                            :
                                            <>
                                                <span className="price">{record.price}</span>
                                                <span className="curency ps-1">{record.currency} / {record.priceTypeStr}</span>
                                            </>
                                    }
                                </span>
                            </label>
                            <label className="d-block badge bg-outline-secondary text-center br-0">
                                <span>
                                    {t("offer.requested")}
                                </span>
                            </label>
                        </div>
                        {
                            record.minPrice
                                ?
                                <div className="d-inline-block me-2 mb-2">
                                    <label className="d-block bg-outline-danger br-0">
                                        <span className="badge bg-danger w-100 d-block br-0">

                                            <span className="price">{record.minPrice}</span>
                                            <span className="curency ps-1">{record.currency}</span>
                                        </span>
                                    </label>
                                    <label className="d-block badge bg-outline-danger text-center br-0">
                                        <span>
                                            {t("offer.lowest")}
                                        </span>
                                    </label>
                                </div>
                                :
                                <></>
                        }
                        {
                            record.myMinPrice ?
                                <div className="d-inline-block me-2 mb-2">
                                    <label className="d-block bg-outline-info br-0">
                                        <span className="badge bg-info w-100 d-block br-0">
                                            <span className="price">{record.myMinPrice}</span>
                                            <span className="curency ps-1">{record.currency}</span>
                                        </span>
                                    </label>
                                    <label className="d-block badge bg-outline-info text-center br-0">
                                        <span>
                                            {t("offer.offered")}
                                        </span>
                                    </label>
                                </div>
                                : <></>
                        }
                        {
                            record.isAnswered === enmOfferAnswerType.Watch
                            &&
                            <div className="d-inline-block me-2 mb-2">
                                <label className="d-block bg-outline-info br-0">
                                    <span className="badge bg-info w-100 d-block br-0">
                                        <FontAwesomeIcon icon={faEye} />
                                        <span className="ms-1">{t("offer.watching")}</span>
                                    </span>
                                </label>
                                <label className="d-block badge bg-outline-info text-center br-0">
                                    <span>
                                        {t("offer.status")}
                                    </span>
                                </label>
                            </div>
                        }
                        
                        
                        {
                            record.isAnswered === enmOfferAnswerType.Send &&
                            <>
                            {
                                record.winnerOfferPaximumId ?
                                <>
                                {
                                    record.winnerOfferPaximumId == record.paximumId ?
                                    <div className="d-inline-block me-2 mb-2">
                                        <label className="d-block bg-success br-0">
                                            <span className="badge bg-success w-100 d-block br-0">
                                                <FontAwesomeIcon icon={faTrophy} />
                                                <span className="ms-1">{t("offer.winner")}</span>
                                            </span>
                                        </label>
                                        <label className="d-block badge bg-outline-success text-center br-0">
                                            <span>
                                                {t("offer.result")}
                                            </span>
                                        </label>
                                    </div>
                                    :
                                    <div className="d-inline-block me-2 mb-2">
                                        <label className="d-block bg-danger br-0">
                                            <span className="badge bg-danger w-100 d-block br-0">
                                                <FontAwesomeIcon icon={faThumbsDown} />
                                                <span className="ms-1">{t("offer.loser")}</span>
                                            </span>
                                        </label>
                                        <label className="d-block badge bg-outline-danger text-center br-0">
                                            <span>
                                                {t("offer.result")}
                                            </span>
                                        </label>
                                    </div>
                                }
                                </>
                                :
                                <>
                                    <div className="d-inline-block me-2 mb-2">
                                        <label className="d-block bg-outline-success br-0">
                                            <span className="badge bg-success w-100 d-block br-0">
                                                <FontAwesomeIcon icon={faPaperPlane} />
                                                <span className="ms-1">{t("offer.sent")}</span>
                                            </span>
                                        </label>
                                        <label className="d-block badge bg-outline-success text-center br-0">
                                            <span>
                                                {t("offer.status")}
                                            </span>
                                        </label>
                                    </div>
                                </>
                            }
                                
                            </>
                        }
                        {
                            record.isAnswered === enmOfferAnswerType.Ignore
                            &&
                            <div className="d-inline-block me-2 mb-2">
                                <label className="d-block bg-outline-danger br-0">
                                    <span className="badge bg-danger w-100 d-block br-0">
                                        <FontAwesomeIcon icon={faTimes} />
                                        <span className="ms-1">{t("offer.ignored")}</span>
                                    </span>
                                </label>
                                <label className="d-block badge bg-outline-danger text-center br-0">
                                    <span>
                                        {t("offer.status")}
                                    </span>
                                </label>
                            </div>
                        }
                        
                        {
                            record.isAnswered === enmOfferAnswerType.None &&
                            <>
                            {
                                record.winnerOfferPaximumId ?
                                <>
                                {
                                    record.winnerOfferPaximumId == record.paximumId ?
                                    <div className="d-inline-block me-2 mb-2">
                                        <label className="d-block bg-success br-0">
                                            <span className="badge bg-success w-100 d-block br-0">
                                                <FontAwesomeIcon icon={faTrophy} />
                                                <span className="ms-1">{t("offer.winner")}</span>
                                            </span>
                                        </label>
                                        <label className="d-block badge bg-outline-success text-center br-0">
                                            <span>
                                                {t("offer.result")}
                                            </span>
                                        </label>
                                    </div>
                                    :
                                    <div className="d-inline-block me-2 mb-2">
                                        <label className="d-block bg-danger br-0">
                                            <span className="badge bg-danger w-100 d-block br-0">
                                                <FontAwesomeIcon icon={faThumbsDown} />
                                                <span className="ms-1">{t("offer.loser")}</span>
                                            </span>
                                        </label>
                                        <label className="d-block badge bg-outline-danger text-center br-0">
                                            <span>
                                                {t("offer.result")}
                                            </span>
                                        </label>
                                    </div>
                                }
                                </>
                                :
                                <>
                                {
                                    !record.cancelDate ?
                                    <div className="d-inline-block me-2 mb-2">
                                        <label className="d-block bg-outline-warning br-0">
                                            <span className="badge bg-warning w-100 d-block br-0">
                                                <FontAwesomeIcon icon={faClock} />
                                                <span className="ms-1">{t("offer.waiting")}</span>
                                            </span>
                                        </label>
                                        <label className="d-block badge bg-outline-warning text-center br-0">
                                            <span>
                                                {t("offer.status")}
                                            </span>
                                        </label>
                                    </div>
                                    :
                                    <div className="d-inline-block me-2 mb-2">
                                        <label className="d-block bg-outline-dark br-0">
                                            <span className="badge bg-dark w-100 d-block br-0">
                                                <FontAwesomeIcon icon={faBan} />
                                                <span className="ms-1">{t("offer.cancelled")}</span>
                                            </span>
                                        </label>
                                        <label className="d-block badge bg-outline-dark text-center br-0">
                                            <span>
                                                {t("offer.status")}
                                            </span>
                                        </label>
                                    </div>
                                }
                                </>
                            }
                                
                            </>
                        }
                    </div>
                </>
            ),
        },
        {
            title: t('common.action'),
            key: 'action',
            fixed: 'right',
            render: (_, record) => (
                <>
                    <div>
                        <Space direction="vertical" size="small" style={{ width: "100%" }}>
                            {getOfferAnswer(record)}
                            <Button className="btn btn-primary btn-block d-block btn-sm" onClick={() => showDetail(record)}>{t("common.detail")}</Button>
                        </Space>
                    </div>
                </>
            ),
        }
    ];

    const getOfferAnswer = (record: mdlOffer) => {
        return <>
            {
                record.isAnswered === enmOfferAnswerType.Watch
                &&
                <span className="d-block badge bg-info text-center">
                    <FontAwesomeIcon icon={faEye} />
                    <span className="ms-1">{t("offer.watching")}</span>
                </span>
            }
            {
                record.isAnswered === enmOfferAnswerType.Send && !record.winnerOfferPaximumId
                &&
                <span className="d-block badge bg-success text-center">
                    <FontAwesomeIcon icon={faPaperPlane} />
                    <span className="ms-1">{t("offer.sent")}</span>
                </span>
            }
            {
                record.isAnswered === enmOfferAnswerType.Ignore
                &&
                <span className="d-block badge bg-danger text-center">
                    <FontAwesomeIcon icon={faTimes} />
                    <span className="ms-1">{t("offer.ignored")}</span>
                </span>
            }
            {
                record.isAnswered === enmOfferAnswerType.None && !record.winnerOfferPaximumId && !record.cancelDate
                &&
                <span className="d-block badge bg-warning text-center">
                    <FontAwesomeIcon icon={faClock} />
                    <span className="ms-1">{t("offer.waiting")}</span>
                </span>
            }
            {
                record.cancelDate
                &&
                <span className="d-block badge bg-danger text-center">
                    <FontAwesomeIcon icon={faBan} />
                    <span className="ms-1">{t("offer.cancelled")}</span>
                </span>
            }
            {
                getRemainingTimes(record)?.some(n => n < 0) ||
                (record.isAnswered === enmOfferAnswerType.None && record.winnerOfferPaximumId && record.winnerOfferPaximumId != record.paximumId )
                &&
                <span className="d-block badge bg-danger text-center">
                    <FontAwesomeIcon icon={faTimes} />
                    <span className="ms-1">{t("offer.terminated")}</span>
                </span>
            }
        </>;
    }

    const handleRowDoubleClick = (e: React.MouseEvent<HTMLInputElement>, offer: mdlOffer) => {
        showDetail(offer);
    }

    return (
        <>
            <div className="row">
                {
                    props.tableItems
                    &&
                    <div className="col-sm-12">
                        <Table<mdlOffer>
                            size="middle"
                            columns={columns}
                            scroll={{ x: 'max-content' }}
                            pagination={false}
                            dataSource={props.tableItems.map((data: mdlOffer, index: number) => ({ ...data, key: index }))}
                            onRow={(offer: mdlOffer) => ({
                                onDoubleClick: (e) => handleRowDoubleClick(e, offer),
                            })}
                        />
                    </div>
                }

                {
                    !props.tableItems
                    &&
                    <div className="col-sm-12">
                        <div className="alert alert-warning alert-icon">
                            <FontAwesomeIcon icon={faWarning} />
                            <span className="ps-4">{t('common.dataNotFound')}</span>
                        </div>
                    </div>
                }
            </div>
        </>
    )
})